/**
 * Keep the variant picker on a combination that exists, 2026-08-01.
 *
 * THE BUG. Colours do not all carry the same sizes — on the cropped tee, Navy and
 * Brown have no XS. Pick Navy while XS is selected and Shopify resolves no variant
 * at all, so `product-info.js` takes this path:
 *
 *     if (!variant) { this.setUnavailable(); return; }   // product-info.js:173
 *
 * and returns ONE LINE before `updateMedia()`. The colour changes, the gallery does
 * not, and the customer is left on a black photo with an UNAVAILABLE button. Verified
 * on the live storefront 2026-08-01: `data-selected-variant` null, gallery still
 * alt="Black", button "UNAVAILABLE".
 *
 * THE FIX. Correct the OTHER options to something that exists before Dawn ever sees
 * the change, so a real variant resolves and Dawn's own path updates media, price,
 * SKU and button with no special-casing. Navy + XS silently becomes Navy + S.
 *
 * WHY A CAPTURE LISTENER RATHER THAN AN EDIT TO DAWN. `VariantSelects` listens for
 * `change` on itself in the bubble phase (global.js:1082) and reads the checked
 * inputs via its `selectedOptionValues` getter. A capture-phase listener on the
 * document runs while the event is still descending, so the DOM is already corrected
 * by the time that getter is called. Nothing in `global.js` or `product-info.js` is
 * touched, and if this file fails to load the picker behaves exactly as it does
 * today — the failure mode is the current bug, not a worse one.
 *
 * DELIBERATELY NOT DISPATCHED. Setting `.checked` does not fire `change`, which is
 * what keeps this from recursing. Do not "fix" that by dispatching one.
 */
(() => {
  const MATRIX_SELECTOR = 'script.pe-variant-matrix';

  /** Each option renders one `.product-form__input` block, in option order. */
  const optionGroups = (selects) => Array.from(selects.querySelectorAll('.product-form__input'));

  /**
   * The value currently chosen in a group.
   *
   * THE CHANGED DROPDOWN AND THE UNTOUCHED ONES MUST BE READ DIFFERENTLY, and
   * getting that wrong silently disables this whole file for dropdown pickers.
   * `selectedOptionValues` reads `option[selected]` — the ATTRIBUTE — but Dawn only
   * moves that attribute in `updateSelectionMetadata` (global.js:1096), which runs
   * from its bubble-phase listener, i.e. AFTER this capture-phase one. So on the
   * group the customer just changed the attribute still points at the previous
   * option, and reading it would report the old value: the combination would look
   * valid, the guard below would return, and the gallery bug would be back with
   * nothing failing. Untouched groups have no such race and are read from the
   * attribute, matching what Dawn will send.
   */
  const chosenValue = (group, isChanged) => {
    const select = group.querySelector('select');
    if (select) {
      if (isChanged) return select.selectedOptions[0]?.value ?? select.value;
      return select.querySelector('option[selected]')?.value ?? select.value;
    }
    return group.querySelector('input:checked')?.value ?? null;
  };

  const applyValue = (group, value) => {
    const select = group.querySelector('select');
    if (select) {
      Array.from(select.options).forEach((option) => {
        option.toggleAttribute('selected', option.value === value);
        if (option.value === value) select.value = value;
      });
      return;
    }
    const input = Array.from(group.querySelectorAll('input')).find((i) => i.value === value);
    if (input) input.checked = true;
  };

  const readMatrix = (selects) => {
    const node = selects.querySelector(MATRIX_SELECTOR);
    if (!node) return null;
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch {
      /* A malformed matrix must not take the picker down with it. */
      return null;
    }
  };

  const sameCombination = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

  /**
   * Best existing combination that KEEPS the option the customer just touched.
   * Ranked by how many of their other choices survive, then by availability — so a
   * correction changes as little as possible, and prefers something in stock once
   * stock exists. Ties fall to matrix order, which is Shopify's variant order and
   * therefore the option order shown in the picker (XS, S, M ... not alphabetical).
   */
  const bestMatch = (matrix, wanted, lockedIndex) => {
    let best = null;
    let bestScore = -1;

    matrix.forEach((entry) => {
      const combo = entry.o;
      if (!Array.isArray(combo) || combo[lockedIndex] !== wanted[lockedIndex]) return;

      let score = 0;
      for (let i = 0; i < wanted.length; i += 1) {
        if (i !== lockedIndex && combo[i] === wanted[i]) score += 2;
      }
      if (entry.a) score += 1;

      if (score > bestScore) {
        bestScore = score;
        best = combo;
      }
    });

    return best;
  };

  const onChange = (event) => {
    const input = event.target;
    if (!input || (input.tagName !== 'INPUT' && input.tagName !== 'SELECT')) return;

    const selects = input.closest('variant-selects');
    if (!selects) return;

    const matrix = readMatrix(selects);
    if (!matrix) return;

    const groups = optionGroups(selects);
    /* One option means every combination is reachable; nothing to correct. */
    if (groups.length < 2) return;

    const changedGroup = input.closest('.product-form__input');
    const lockedIndex = groups.indexOf(changedGroup);
    if (lockedIndex === -1) return;

    const wanted = groups.map((group, i) => chosenValue(group, i === lockedIndex));
    /* `!v` rather than `v === null`: a select with no options yields ''. */
    if (wanted.some((v) => !v)) return;

    /* Already a real combination — leave the customer's choice alone. */
    if (matrix.some((entry) => Array.isArray(entry.o) && sameCombination(entry.o, wanted))) return;

    const replacement = bestMatch(matrix, wanted, lockedIndex);
    if (!replacement) return;

    groups.forEach((group, i) => {
      if (i === lockedIndex || replacement[i] === wanted[i]) return;
      applyValue(group, replacement[i]);
    });
  };

  /* Capture phase: runs before VariantSelects' own bubble-phase listener. */
  document.addEventListener('change', onChange, true);
})();
